import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MarketingService } from '@/services/marketing.service';
import { MarketingCampaign, CampaignType, CampaignStatus, EmailCampaign, EmailStatus } from '@/models/marketing.model';
import { useAuth } from '@/contexts/AuthContext';

export const CampaignForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const isEditing = !!id;

  const [formData, setFormData] = useState<Partial<MarketingCampaign>>({
    name: '',
    type: CampaignType.email,
    status: CampaignStatus.draft,
    startDate: new Date(),
    endDate: new Date(),
    budget: 0,
    spent: 0,
    description: '',
  });

  // Email-specific fields
  const [emailData, setEmailData] = useState<{
    subject: string;
    fromEmail: string;
    fromName: string;
    templateId: string;
    htmlContent: string;
    scheduledAt: string;
  }>({
    subject: '',
    fromEmail: profile?.email || 'noreply@faircare.com',
    fromName: 'FairCare',
    templateId: '',
    htmlContent: '',
    scheduledAt: '',
  });

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const isEmailCampaign = formData.type === CampaignType.email;

  const { data: campaign, isLoading } = useQuery({
    queryKey: ['marketingCampaign', id],
    queryFn: () => id ? MarketingService.getCampaignById(id) : null,
    enabled: isEditing && !!id,
  });

  // Load email templates for email campaigns
  const { data: emailTemplates = [] } = useQuery({
    queryKey: ['emailTemplates'],
    queryFn: () => MarketingService.getAllEmailTemplates(),
    enabled: isEmailCampaign,
  });

  React.useEffect(() => {
    if (campaign) {
      setFormData(campaign);
    }
  }, [campaign]);

  // Load template content when template is selected
  React.useEffect(() => {
    if (emailData.templateId && emailTemplates.length > 0) {
      const template = emailTemplates.find(t => t.id === emailData.templateId);
      if (template) {
        setEmailData(prev => ({
          ...prev,
          subject: template.subject,
          htmlContent: template.htmlContent,
        }));
      }
    }
  }, [emailData.templateId, emailTemplates]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (isEmailCampaign) {
        // Create email campaign
        const emailCampaignData: Partial<EmailCampaign> = {
          name: formData.name || '',
          subject: emailData.subject,
          fromEmail: emailData.fromEmail,
          fromName: emailData.fromName,
          templateId: emailData.templateId || undefined,
          htmlContent: emailData.htmlContent,
          status: EmailStatus.draft,
          scheduledAt: emailData.scheduledAt ? new Date(emailData.scheduledAt) : undefined,
          recipientCount: 0,
          segments: [],
          createdBy: profile?.id || '',
          createdByName: profile?.fullName || 'Unknown',
        };
        await MarketingService.createEmailCampaign(emailCampaignData);
      } else {
        // Create regular marketing campaign
        if (isEditing && id) {
          await MarketingService.updateCampaign(id, formData);
        } else {
          await MarketingService.createCampaign(formData);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketingCampaigns'] });
      queryClient.invalidateQueries({ queryKey: ['emailCampaigns'] });
      navigate('/marketing');
    },
    onError: (error: any) => {
      setErrorMessage(error?.message || 'Failed to save campaign');
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!formData.name?.trim()) {
      setErrorMessage('Campaign name is required');
      return;
    }

    if (isEmailCampaign) {
      if (!emailData.subject?.trim()) {
        setErrorMessage('Email subject is required');
        return;
      }
      if (!emailData.htmlContent?.trim()) {
        setErrorMessage('Email content is required');
        return;
      }
    } else {
      if (!formData.startDate || !formData.endDate) {
        setErrorMessage('Start Date and End Date are required');
        return;
      }
    }

    mutation.mutate();
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        {isEditing ? 'Edit Campaign' : 'New Campaign'}
      </h1>

      <form onSubmit={handleSubmit} className="bg-white shadow rounded-lg p-6 space-y-6">
        {errorMessage && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-sm text-red-700">{errorMessage}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">Campaign Name *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Type *</label>
            <select
              required
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as CampaignType })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              {Object.values(CampaignType).map(type => (
                <option key={type} value={type}>{type.replace('_', ' ')}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Status *</label>
            <select
              required
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as CampaignStatus })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              {Object.values(CampaignStatus).map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>

          {!isEmailCampaign && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700">Start Date *</label>
                <input
                  type="date"
                  required
                  value={formData.startDate ? new Date(formData.startDate).toISOString().split('T')[0] : ''}
                  onChange={(e) => setFormData({ ...formData, startDate: new Date(e.target.value) })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">End Date *</label>
                <input
                  type="date"
                  required
                  value={formData.endDate ? new Date(formData.endDate).toISOString().split('T')[0] : ''}
                  onChange={(e) => setFormData({ ...formData, endDate: new Date(e.target.value) })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700">Budget</label>
            <input
              type="number"
              step="0.01"
              value={formData.budget || 0}
              onChange={(e) => setFormData({ ...formData, budget: parseFloat(e.target.value) || 0 })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Spent</label>
            <input
              type="number"
              step="0.01"
              value={formData.spent || 0}
              onChange={(e) => setFormData({ ...formData, spent: parseFloat(e.target.value) || 0 })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Email Campaign Specific Fields */}
          {isEmailCampaign && (
            <>
              <div className="md:col-span-2 border-t pt-6 mt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Email Details</h3>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Email Template</label>
                <select
                  value={emailData.templateId}
                  onChange={(e) => setEmailData({ ...emailData, templateId: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">-- Select a template (optional) --</option>
                  {emailTemplates.filter(t => t.isActive).map(template => (
                    <option key={template.id} value={template.id}>{template.name}</option>
                  ))}
                </select>
                <p className="mt-1 text-sm text-gray-500">Selecting a template will pre-fill the subject and content</p>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Subject *</label>
                <input
                  type="text"
                  required
                  value={emailData.subject}
                  onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Email subject line"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">From Email *</label>
                <input
                  type="email"
                  required
                  value={emailData.fromEmail}
                  onChange={(e) => setEmailData({ ...emailData, fromEmail: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">From Name *</label>
                <input
                  type="text"
                  required
                  value={emailData.fromName}
                  onChange={(e) => setEmailData({ ...emailData, fromName: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Scheduled Send Date (optional)</label>
                <input
                  type="datetime-local"
                  value={emailData.scheduledAt}
                  onChange={(e) => setEmailData({ ...emailData, scheduledAt: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="mt-1 text-sm text-gray-500">Leave empty to send immediately when campaign is activated</p>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Email Content (HTML) *</label>
                <textarea
                  required
                  value={emailData.htmlContent}
                  onChange={(e) => setEmailData({ ...emailData, htmlContent: e.target.value })}
                  rows={12}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                  placeholder="Enter HTML content for the email..."
                />
                <p className="mt-1 text-sm text-gray-500">You can use HTML tags to format your email</p>
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/marketing')}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            {mutation.isPending ? 'Saving...' : isEditing ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
};


